import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

interface ReverseTransitionProps {
  isActive: boolean;
  onTransitionComplete: () => void;
  originRect?: DOMRect | null;
}

/**
 * A contracting ink transition for going back.
 * Starts fully covering the screen and contracts to reveal what's underneath.
 * Simplified for smooth performance on iOS - matches WavyTransition pattern.
 */
export function ReverseTransition({
  isActive,
  onTransitionComplete,
  originRect,
}: ReverseTransitionProps) {
  const [phase, setPhase] = useState<'idle' | 'contracting' | 'fading' | 'complete'>('idle');
  const [progress, setProgress] = useState(0);
  const [fadeProgress, setFadeProgress] = useState(0);
  const animationRef = useRef<number | null>(null);

  // Calculate origin position (where the back button was pressed)
  const originX = originRect 
    ? originRect.left + originRect.width / 2
    : 40;
  const originY = originRect 
    ? originRect.top + originRect.height / 2
    : 60;

  // Calculate max radius needed to cover entire screen from the origin point
  const maxRadius = useMemo(() => {
    if (typeof window === 'undefined') return 2000;
    const corners = [
      Math.sqrt(originX * originX + originY * originY),
      Math.sqrt((window.innerWidth - originX) ** 2 + originY ** 2),
      Math.sqrt(originX ** 2 + (window.innerHeight - originY) ** 2),
      Math.sqrt((window.innerWidth - originX) ** 2 + (window.innerHeight - originY) ** 2),
    ];
    return Math.max(...corners) * 1.2;
  }, [originX, originY]);

  // Generate an organic blobby path that contracts - matches WavyTransition pattern
  const generateInkPath = useCallback((
    contractProgress: number,
    seed: number,
    cx: number,
    cy: number,
    radius: number
  ) => {
    // contractProgress goes 0->1, we want to shrink, so use (1 - contractProgress)
    const currentRadius = radius * (1 - contractProgress);
    if (currentRadius < 1) return '';

    const points: [number, number][] = [];
    const segments = 72;

    for (let i = 0; i < segments; i++) {
      const angle = (i / segments) * Math.PI * 2;

      // Layer multiple sine waves for organic blobby effect - same as WavyTransition
      const noise1 = Math.sin(angle * 3 + seed) * 0.15;
      const noise2 = Math.sin(angle * 7 + seed * 2.3) * 0.08;
      const noise3 = Math.sin(angle * 11 + seed * 1.7) * 0.05;
      const noise4 = Math.sin(angle * 5 + contractProgress * Math.PI * 4) * 0.1 * (1 - contractProgress);

      const noiseTotal = 1 + noise1 + noise2 + noise3 + noise4;
      const r = currentRadius * noiseTotal;

      const x = cx + r * Math.cos(angle);
      const y = cy + r * Math.sin(angle);

      points.push([x, y]);
    }

    if (points.length < 3) return '';

    // Create smooth bezier curve - same as WavyTransition
    let path = `M ${points[0][0]} ${points[0][1]}`;

    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];

      const tension = 0.3;
      const cp1x = p1[0] + (p2[0] - p0[0]) * tension;
      const cp1y = p1[1] + (p2[1] - p0[1]) * tension;
      const cp2x = p2[0] - (p3[0] - p1[0]) * tension;
      const cp2y = p2[1] - (p3[1] - p1[1]) * tension;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
    }

    path += ' Z';
    return path;
  }, []);

  // Start contracting animation when active
  useEffect(() => {
    if (isActive && phase === 'idle') {
      setPhase('contracting');
      setFadeProgress(0);
      setProgress(0);

      const startTime = performance.now();
      const duration = 700;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const rawProgress = Math.min(elapsed / duration, 1);

        // Custom easing - slow start, accelerate, then slow end (same as WavyTransition)
        const eased = rawProgress < 0.5
          ? 4 * rawProgress * rawProgress * rawProgress
          : 1 - Math.pow(-2 * rawProgress + 2, 3) / 2;

        setProgress(eased);

        if (rawProgress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Start fade phase
          setPhase('fading');
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    }
  }, [isActive, phase]);

  // Fade out animation
  useEffect(() => {
    if (phase === 'fading') {
      const startTime = performance.now();
      const duration = 250;

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const rawProgress = Math.min(elapsed / duration, 1);

        const eased = 1 - Math.pow(1 - rawProgress, 2);
        setFadeProgress(eased);

        if (rawProgress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          setPhase('complete');
          onTransitionComplete();
        }
      };

      animationRef.current = requestAnimationFrame(animate);
    }
  }, [phase, onTransitionComplete]);

  // Reset when deactivated
  useEffect(() => {
    if (!isActive && phase !== 'idle') {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      setPhase('idle');
      setProgress(0);
      setFadeProgress(0);
    }
  }, [isActive, phase]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // Generate multiple ink layers with different seeds for depth - same pattern as WavyTransition
  const inkPaths = useMemo(() => {
    return [
      { path: generateInkPath(progress, 0, originX, originY, maxRadius) },
      { path: generateInkPath(progress * 0.92, 1.5, originX, originY, maxRadius) },
      { path: generateInkPath(progress * 0.84, 3.2, originX, originY, maxRadius) },
      { path: generateInkPath(progress * 0.76, 4.8, originX, originY, maxRadius) },
      { path: generateInkPath(progress * 0.68, 6.1, originX, originY, maxRadius) },
    ];
  }, [progress, generateInkPath, originX, originY, maxRadius]);

  // Render immediately when isActive to prevent flash
  if (!isActive && phase === 'idle') {
    return null;
  }

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 800;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 600;

  // Overall opacity for fade out at the end
  const inkOpacity = 1 - fadeProgress;

  return (
    <div className="fixed inset-0 z-[100] overflow-hidden pointer-events-none">
      {/* SVG ink layers that contract - no transforms on SVG, no filters (iOS-friendly) */}
      {inkOpacity > 0 && (
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
          preserveAspectRatio="none"
          style={{ overflow: 'visible', opacity: inkOpacity }}
        >
          <defs>
            {/* Static gradients - no gradientTransform for iOS performance */}
            <linearGradient id="reverseInkGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#38bdf8" />
              <stop offset="25%" stopColor="#a78bfa" />
              <stop offset="50%" stopColor="#f472b6" />
              <stop offset="75%" stopColor="#fb7185" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
            <linearGradient id="reverseInkGradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#34d399" />
              <stop offset="33%" stopColor="#38bdf8" />
              <stop offset="66%" stopColor="#a78bfa" />
              <stop offset="100%" stopColor="#f472b6" />
            </linearGradient>
            <linearGradient id="reverseInkGradient3" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#fbbf24" />
              <stop offset="50%" stopColor="#fb7185" />
              <stop offset="100%" stopColor="#34d399" />
            </linearGradient>
            <linearGradient id="reverseInkGradient4" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#a78bfa" />
              <stop offset="50%" stopColor="#fbbf24" />
              <stop offset="100%" stopColor="#38bdf8" />
            </linearGradient>
            <linearGradient id="reverseInkGradient5" x1="0%" y1="50%" x2="100%" y2="50%">
              <stop offset="0%" stopColor="#f472b6" />
              <stop offset="50%" stopColor="#fb7185" />
              <stop offset="100%" stopColor="#fbbf24" />
            </linearGradient>
          </defs>

          {/* Ink layers that contract - no filters for smooth iOS performance */}
          {inkPaths.map(({ path }, i) => {
            const baseOpacity = 1 - i * 0.15;
            return path ? (
              <path
                key={`ink-${i}`}
                d={path}
                fill={`url(#reverseInkGradient${i + 1})`}
                opacity={baseOpacity}
              />
            ) : null;
          })}
        </svg>
      )}
    </div>
  );
}
